import apiInstance from '@/api';
import toastMessage from '@/components/Toast';
import { ThunkApiConfig } from '@/models/ThunkApiConfig';
import {
  BackgroundTask,
  RevalueType,
} from '@/pages/tools/RevaluationTool/types';
import { getThunkError } from '@/store/utility/thunkErrorUtils';
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  TASK_POLL_LIMIT,
  isTaskActive,
  isTaskTerminal,
  revalueTypeLabel,
} from './constants';

export type PollBackgroundTasksResult = {
  tasks: BackgroundTask[];
  newlyCompletedIds: string[];
};

export type CancelResult = {
  /** Ids the backend accepted for cancellation — marked "cancelling" until a poll confirms terminal status. */
  cancelledIds: string[];
};

/**
 * Polls the background-task listing endpoint. We request finished history too
 * (`active=false`) so a single call gives both the in-flight list (for the
 * badge/progress) and the recent completions (for the "done" ring + history).
 *
 * Completion is detected by diffing against the previously known list: any task
 * that was active last poll and is terminal now just finished — we surface a
 * toast for it and flag the bell so it rings until the user opens the panel.
 */
export const pollBackgroundTasks = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'backgroundTasks/poll',
  async (_, { getState }): Promise<PollBackgroundTasksResult> => {
    const response = await apiInstance.revalueTrades.listTasks({
      active: false,
      limit: TASK_POLL_LIMIT,
    });
    const tasks: BackgroundTask[] = response.data?.data ?? [];

    const previousTasks = getState().backgroundTasks.tasks;
    const previouslyActiveIds = new Set(
      previousTasks.filter(isTaskActive).map((t) => t.task_id),
    );

    const newlyCompleted = tasks.filter(
      (t) => isTaskTerminal(t.status) && previouslyActiveIds.has(t.task_id),
    );

    newlyCompleted.forEach((task) => {
      const label = revalueTypeLabel(task.revalue_type);
      if (task.statistic?.error) {
        toastMessage.error(
          `${label} revaluation failed: ${task.statistic.error}`,
        );
      } else {
        toastMessage.success(`${label} revaluation finished.`);
      }
    });

    return {
      tasks,
      newlyCompletedIds: newlyCompleted.map((t) => t.task_id),
    };
  },
  {
    // Skip overlapping polls (the interval tick and a manual refresh on open).
    condition: (_, { getState }) => !getState().backgroundTasks.loading,
  },
);

/** Cooperatively cancels a single run; it stops at its next trade boundary. */
export const cancelBackgroundTask =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'backgroundTasks/cancelOne',
    async (taskId: string, { dispatch }): Promise<CancelResult> => {
      try {
        await apiInstance.revalueTrades.cancelTask(taskId);
        toastMessage.success('Revaluation cancellation requested.');
        return { cancelledIds: [taskId] };
      } catch (error: any) {
        toastMessage.error(
          `Failed to cancel revaluation: ${getThunkError(error)}`,
        );
        return { cancelledIds: [] };
      } finally {
        dispatch(pollBackgroundTasks());
      }
    },
  );

/**
 * Cancels all of the current user's queued + running runs (optionally scoped to
 * one revalue type), then refreshes the list.
 */
export const cancelAllBackgroundTasks =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'backgroundTasks/cancelAll',
    async (
      revalueType: RevalueType | undefined,
      { dispatch, getState },
    ): Promise<CancelResult> => {
      try {
        const response = await apiInstance.revalueTrades.cancelAllTasks(
          revalueType ? { revalue_type: revalueType } : {},
        );
        const count = response.data?.data?.cancelled_count ?? 0;
        // Prefer the ids the backend reports; fall back to the active ids we
        // know about so the UI still updates optimistically.
        const cancelledIds: string[] =
          response.data?.data?.cancelled_task_ids ??
          getState()
            .backgroundTasks.tasks.filter(isTaskActive)
            .map((t) => t.task_id);
        toastMessage.success(
          count > 0
            ? `Cancellation requested for ${count} task${count === 1 ? '' : 's'}.`
            : 'No running tasks to cancel.',
        );
        return { cancelledIds };
      } catch (error: any) {
        toastMessage.error(
          `Failed to cancel revaluations: ${getThunkError(error)}`,
        );
        return { cancelledIds: [] };
      } finally {
        dispatch(pollBackgroundTasks());
      }
    },
  );
